import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, OverlayTrigger, Tooltip, Collapse, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faChevronDown, faChevronUp, faFingerprint, faCreditCard } from '@fortawesome/free-solid-svg-icons';

const PagoRecibidoCard = ({ pago, isSelected = false, onToggleSelection }) => {
    const [expanded, setExpanded] = useState(false);

    // Parsear documentos relacionados del campo JSON embebido en la fila
    const documentosRelacionados = (() => {
        try {
            return pago.documentosRelacionados ? JSON.parse(pago.documentosRelacionados) : [];
        } catch {
            return [];
        }
    })();

    const {
        idFacturaPagoRecibido,
        vSerie,
        vFolio,
        dFechaEmision,
        dFechaTimbrado,
        FechaPago,
        vRfcEmisor,
        vRazonSocialEmisor,
        FormaDePagoP,
        MonedaP,
        dTipoCambio,
        MontoTotalPagos,
        bIsActive,
        NumOperacion,
        vUUIDFactura,
        TotalTrasladosBaseIVA16,
        TotalTrasladosImpuestoIVA16,
        TotalTrasladosBaseIVA8,
        TotalTrasladosImpuestoIVA8,
        TotalTrasladosBaseIVA0,
        TotalTrasladosImpuestoIVA0,
        TotalTrasladosBaseIVAExento,
        TotalRetencionesIEPS,
        TotalRetencionesISR,
        TotalRetencionesIVA,
        vNumCta,
        numCtaAlias,
        MonedaPago,
        RegimenFiscalEmisorCodigo,
        RegimenFiscalEmisorDesc
    } = pago;

    const isInactive = bIsActive === false;
    const url = `/contabilidad/centro-xml/pagos-recibidos/visualizacion/${idFacturaPagoRecibido}`;

    const handleCardClick = (e) => {
        if (e.target.tagName === 'A' || e.target.closest('a') || e.target.closest('button')) {
            return;
        }
        if (onToggleSelection) {
            onToggleSelection(idFacturaPagoRecibido);
        }
    };

    const cardClasses = [
        'mb-3',
        'shadow-sm',
        isInactive ? 'border-danger' : '',
        isSelected ? 'border-primary border-3' : ''
    ].filter(Boolean).join(' ');

    const cardStyle = {
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        ...(isSelected && {
            backgroundColor: 'rgba(13, 110, 253, 0.1)',
            boxShadow: '0 0 0 0.2rem rgba(13, 110, 253, 0.25)'
        })
    };

    const formatCurrency = (value) => {
        if (value == null) return '—';
        return parseFloat(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString("es-MX", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        }).replace(/(?:^|\s)([a-z])/g, char => char.toUpperCase());
    };

    return (
        <Card className={cardClasses} onClick={handleCardClick} style={cardStyle}>
            <Card.Body>
                <Row className="align-items-center">
                    {/* Serie, Folio y Fechas */}
                    <Col xs={12} md={3} className="mb-2 mb-md-0">
                        <div className="d-flex flex-column">
                            <div className="d-flex align-items-center gap-2 mb-1">
                                {isSelected && (
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-primary" size="lg" />
                                )}
                                <span className={`fw-bold ${isInactive ? 'text-danger' : 'text-primary'}`} style={{ fontSize: '1.1rem' }}>
                                    <a href={url} target="_blank" rel="noopener noreferrer" className={isInactive ? 'text-danger text-decoration-none' : 'text-primary text-decoration-none'}>
                                        {(vSerie || vFolio) ? `${vSerie || ''}${vSerie && vFolio ? '-' : ''}${vFolio || ''}` : ''}
                                    </a>
                                </span>
                                <a href={url} target="_blank" rel="noopener noreferrer">
                                    <img title="Ver información" src="/img/ver20.png" alt="ver" style={{ width: '20px' }} />
                                </a>
                                {isInactive && (
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Esta factura no está siendo tomada en cuenta</Tooltip>}>
                                        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: 'red', display: 'inline-block' }}></div>
                                    </OverlayTrigger>
                                )}
                            </div>
                            <small className={isInactive ? 'text-danger' : 'text-muted'}>
                                <strong>Emisión:</strong> {formatDate(dFechaEmision)}
                            </small>
                            <small className={isInactive ? 'text-danger' : 'text-muted'}>
                                <strong>Timbrado:</strong> {formatDate(dFechaTimbrado)}
                            </small>
                            <small className={isInactive ? 'text-danger' : 'text-muted'}>
                                <strong>Pago:</strong> {formatDate(FechaPago)}
                            </small>
                        </div>
                    </Col>

                    {/* Emisor */}
                    <Col xs={12} md={6} className="mb-2 mb-md-0">
                        <div className="mb-2">
                            <h6 className="mb-1 text-muted" style={{ fontSize: '0.85rem' }}>Emisor</h6>
                            <h6 className={`mb-1 ${isInactive ? 'text-danger' : ''}`} title={vRazonSocialEmisor}>
                                <span className="text-truncate d-inline-block">
                                    {vRfcEmisor && vRfcEmisor !== 'XEXX010101000' && (
                                        <img src="/img/bandera-mexico-48.png" width="20" alt="México" className="me-1" />
                                    )}
                                    {vRazonSocialEmisor}
                                </span>
                            </h6>
                            <div className={`small mb-1 ${isInactive ? 'text-danger' : 'text-muted'}`}>
                                <strong>RFC:</strong> {vRfcEmisor}
                            </div>
                            {RegimenFiscalEmisorCodigo && (
                                <div className={`small mb-1 ${isInactive ? 'text-danger' : 'text-muted'}`} title={RegimenFiscalEmisorDesc}>
                                    <strong>Régimen Fiscal:</strong> {RegimenFiscalEmisorCodigo}{RegimenFiscalEmisorDesc ? ` - ${RegimenFiscalEmisorDesc}` : ''}
                                </div>
                            )}

                            {/* UUID */}
                            {vUUIDFactura && (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Click para ver factura - UUID: {vUUIDFactura}</Tooltip>}
                                >
                                    <div className={`small mt-2 ${isInactive ? 'text-danger' : 'text-muted'}`}>
                                        <FontAwesomeIcon icon={faFingerprint} className="me-1" />
                                        <strong>UUID:</strong>{' '}
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-decoration-none d-inline-block"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <span className="d-inline-block text-primary" style={{ textDecoration: 'underline' }}>
                                                {vUUIDFactura}
                                            </span>
                                        </a>
                                    </div>
                                </OverlayTrigger>
                            )}
                        </div>
                    </Col>

                    {/* Totales y Forma de Pago */}
                    <Col xs={12} md={3} className="text-md-end">
                        <div className="d-flex flex-column align-items-md-end">
                            <span className={`fw-bold mb-1 ${isInactive ? 'text-danger' : 'text-success'}`} style={{ fontSize: '1.2rem' }}>
                                {parseFloat(MontoTotalPagos).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}
                            </span>
                            <div className={`small mb-1 ${isInactive ? 'text-danger' : 'text-muted'}`}>
                                <strong>Moneda:</strong> {MonedaP}
                                {MonedaPago && MonedaPago !== MonedaP && (
                                    <span className="ms-1 text-muted">/ Pago: {MonedaPago}</span>
                                )}
                                {dTipoCambio != 1 && (
                                    <span className="ms-1">(T.C. {dTipoCambio})</span>
                                )}
                            </div>
                            {FormaDePagoP && (
                                <div className="mb-1">
                                    <Badge bg="light" text="dark" className="border">
                                        <FontAwesomeIcon icon={faCreditCard} className="me-1 text-muted" />
                                        {FormaDePagoP}
                                    </Badge>
                                </div>
                            )}
                            <button className="btn btn-link btn-sm p-0 mt-1" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
                                {expanded ? 'Ocultar detalles' : 'Ver más'} <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                            </button>
                        </div>
                    </Col>
                </Row>

                <Collapse in={expanded}>
                    <div className="mt-3 pt-3 border-top">
                        {/* Datos fiscales generales */}
                        <Row>
                            <Col md={6}>
                                {NumOperacion && <p className="mb-1 small"><strong>Núm. Operación:</strong> {NumOperacion}</p>}
                                {(vNumCta || numCtaAlias) && <p className="mb-1 small"><strong>Cuenta Bancaria:</strong> {numCtaAlias || ''}{vNumCta ? ` (${vNumCta})` : ''}</p>}
                            </Col>
                            <Col md={6}>
                                <div className="row small">
                                    <div className="col-6">
                                        {TotalTrasladosBaseIVA16 != null && <p className="mb-0"><strong>Base IVA 16%:</strong> {formatCurrency(TotalTrasladosBaseIVA16)}</p>}
                                        {TotalTrasladosImpuestoIVA16 != null && <p className="mb-0"><strong>IVA 16%:</strong> {formatCurrency(TotalTrasladosImpuestoIVA16)}</p>}
                                        {TotalTrasladosBaseIVA8 != null && <p className="mb-0"><strong>Base IVA 8%:</strong> {formatCurrency(TotalTrasladosBaseIVA8)}</p>}
                                        {TotalTrasladosImpuestoIVA8 != null && <p className="mb-0"><strong>IVA 8%:</strong> {formatCurrency(TotalTrasladosImpuestoIVA8)}</p>}
                                        {TotalTrasladosBaseIVA0 != null && <p className="mb-0"><strong>Base IVA 0%:</strong> {formatCurrency(TotalTrasladosBaseIVA0)}</p>}
                                        {TotalTrasladosImpuestoIVA0 != null && <p className="mb-0"><strong>IVA 0%:</strong> {formatCurrency(TotalTrasladosImpuestoIVA0)}</p>}
                                        {TotalTrasladosBaseIVAExento != null && <p className="mb-0"><strong>Base Exento:</strong> {formatCurrency(TotalTrasladosBaseIVAExento)}</p>}
                                    </div>
                                    <div className="col-6">
                                        {TotalRetencionesIEPS != null && <p className="mb-0"><strong>Ret. IEPS:</strong> {formatCurrency(TotalRetencionesIEPS)}</p>}
                                        {TotalRetencionesISR != null && <p className="mb-0"><strong>Ret. ISR:</strong> {formatCurrency(TotalRetencionesISR)}</p>}
                                        {TotalRetencionesIVA != null && <p className="mb-0"><strong>Ret. IVA:</strong> {formatCurrency(TotalRetencionesIVA)}</p>}
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Documentos Relacionados */}
                        {documentosRelacionados.length > 0 && (
                            <div className="mt-3">
                                <h6 className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                                    Documentos Relacionados ({documentosRelacionados.length})
                                </h6>
                                {documentosRelacionados.map((doc, i) => {
                                    const traslados = doc.ImpuestosDR?.TrasladosDR?.TrasladoDR
                                        ? (Array.isArray(doc.ImpuestosDR.TrasladosDR.TrasladoDR)
                                            ? doc.ImpuestosDR.TrasladosDR.TrasladoDR
                                            : [doc.ImpuestosDR.TrasladosDR.TrasladoDR])
                                        : [];
                                    const retenciones = doc.ImpuestosDR?.RetencionesDR?.RetencionDR
                                        ? (Array.isArray(doc.ImpuestosDR.RetencionesDR.RetencionDR)
                                            ? doc.ImpuestosDR.RetencionesDR.RetencionDR
                                            : [doc.ImpuestosDR.RetencionesDR.RetencionDR])
                                        : [];

                                    return (
                                        <div key={i} className="border rounded p-2 mb-2" style={{ fontSize: '0.8rem' }}>
                                            {/* Identificación del documento */}
                                            <div className="d-flex flex-wrap gap-3 mb-2">
                                                <div>
                                                    <span className="text-muted">Id Documento: </span>
                                                    {doc.idFacturaRecibida ? (
                                                        <a
                                                            href={`/contabilidad/centro-xml/facturas-recibidas/visualizacion/${doc.idFacturaRecibida}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none d-inline-block"
                                                            title="Ver factura recibida"
                                                            onClick={e => e.stopPropagation()}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <span className="d-inline-block text-primary fw-bold" style={{ textDecoration: 'underline' }}>
                                                                <FontAwesomeIcon icon={faFingerprint} className="me-1" />
                                                                {doc.IdDocumento ? doc.IdDocumento.toUpperCase() : '—'}
                                                            </span>
                                                        </a>
                                                    ) : (
                                                        <strong title={doc.IdDocumento}>
                                                            <FontAwesomeIcon icon={faFingerprint} className="me-1 text-muted" />
                                                            {doc.IdDocumento ? doc.IdDocumento.toUpperCase() : '—'}
                                                        </strong>
                                                    )}
                                                </div>
                                                {doc.Serie && (
                                                    <div>
                                                        <span className="text-muted">Serie: </span>
                                                        <strong>{doc.Serie}</strong>
                                                    </div>
                                                )}
                                                {doc.Folio && (
                                                    <div>
                                                        <span className="text-muted">Folio: </span>
                                                        <strong>{doc.Folio}</strong>
                                                    </div>
                                                )}
                                                {doc.NumParcialidad && (
                                                    <div>
                                                        <span className="text-muted">Núm. Parcialidad: </span>
                                                        <strong>{doc.NumParcialidad}</strong>
                                                    </div>
                                                )}
                                                {doc.MonedaDR && (
                                                    <div>
                                                        <span className="text-muted">Moneda DR: </span>
                                                        <strong>{doc.MonedaDR}</strong>
                                                        {doc.EquivalenciaDR && doc.EquivalenciaDR !== '1' && (
                                                            <span className="text-muted ms-1">(Equiv. {doc.EquivalenciaDR})</span>
                                                        )}
                                                    </div>
                                                )}
                                                {doc.MetodoDePagoDR && (
                                                    <div>
                                                        <span className="text-muted">Método Pago DR: </span>
                                                        <strong>{doc.MetodoDePagoDR}</strong>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Importes */}
                                            <div className="d-flex flex-wrap gap-3 mb-1">
                                                <div>
                                                    <span className="text-muted">Saldo Anterior: </span>
                                                    <strong>{formatCurrency(doc.ImpSaldoAnt)}</strong>
                                                </div>
                                                <div>
                                                    <span className="text-muted">Importe Pagado: </span>
                                                    <strong className="text-success">{formatCurrency(doc.ImpPagado)}</strong>
                                                </div>
                                                <div>
                                                    <span className="text-muted">Saldo Insoluto: </span>
                                                    <strong className={parseFloat(doc.ImpSaldoInsoluto) === 0 ? 'text-success' : 'text-warning'}>
                                                        {formatCurrency(doc.ImpSaldoInsoluto)}
                                                    </strong>
                                                    {parseFloat(doc.ImpSaldoInsoluto) === 0 && (
                                                        <span className="badge bg-success ms-2">Pagado</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Impuestos DR */}
                                            {(traslados.length > 0 || retenciones.length > 0) && (
                                                <div className="border-top pt-2 mt-1 d-flex flex-wrap gap-4">
                                                    {traslados.length > 0 && (
                                                        <div>
                                                            <span className="text-muted fw-semibold">Traslados DR:</span>
                                                            {traslados.map((t, ti) => (
                                                                <div key={ti} className="ms-2">
                                                                    <span className="text-muted">
                                                                        Imp. {t.ImpuestoDR} ({t.TipoFactorDR}{t.TipoFactorDR !== 'Exento' ? ` ${parseFloat(t.TasaOCuotaDR) * 100}%` : ''}):
                                                                    </span>
                                                                    <strong> Base {formatCurrency(t.BaseDR)}{t.TipoFactorDR !== 'Exento' ? ` → ${formatCurrency(t.ImporteDR)}` : ''}</strong>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {retenciones.length > 0 && (
                                                        <div>
                                                            <span className="text-muted fw-semibold">Retenciones DR:</span>
                                                            {retenciones.map((r, ri) => (
                                                                <div key={ri} className="ms-2">
                                                                    <span className="text-muted">
                                                                        Imp. {r.ImpuestoDR} ({r.TipoFactorDR}{r.TipoFactorDR !== 'Exento' ? ` ${parseFloat(r.TasaOCuotaDR) * 100}%` : ''}):
                                                                    </span>
                                                                    <strong> Base {formatCurrency(r.BaseDR)}{r.TipoFactorDR !== 'Exento' ? ` → ${formatCurrency(r.ImporteDR)}` : ''}</strong>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </Collapse>
            </Card.Body>
        </Card>
    );
};

PagoRecibidoCard.propTypes = {
    pago: PropTypes.shape({
        idFacturaPagoRecibido: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        vSerie: PropTypes.string,
        vFolio: PropTypes.string,
        dFechaEmision: PropTypes.string,
        dFechaTimbrado: PropTypes.string,
        FechaPago: PropTypes.string,
        vRfcEmisor: PropTypes.string,
        vRazonSocialEmisor: PropTypes.string,
        vRfcReceptor: PropTypes.string,
        vRazonSocialReceptor: PropTypes.string,
        FormaDePagoP: PropTypes.string,
        MonedaP: PropTypes.string,
        dTipoCambio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        MontoTotalPagos: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        bIsActive: PropTypes.bool,
        NumOperacion: PropTypes.string,
        vUUIDFactura: PropTypes.string,
        TotalTrasladosBaseIVA16: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        TotalTrasladosImpuestoIVA16: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        TotalTrasladosBaseIVA8: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        TotalTrasladosImpuestoIVA8: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        TotalTrasladosBaseIVA0: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        TotalTrasladosImpuestoIVA0: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        TotalTrasladosBaseIVAExento: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        TotalRetencionesIEPS: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        TotalRetencionesISR: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        TotalRetencionesIVA: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        vNumCta: PropTypes.string,
        numCtaAlias: PropTypes.string,
        MonedaPago: PropTypes.string,
        RegimenFiscalReceptorCodigo: PropTypes.string,
        RegimenFiscalReceptorDesc: PropTypes.string,
        RegimenFiscalEmisorCodigo: PropTypes.string,
        RegimenFiscalEmisorDesc: PropTypes.string,
        documentosRelacionados: PropTypes.string
    }).isRequired,
    isSelected: PropTypes.bool,
    onToggleSelection: PropTypes.func
};

export default PagoRecibidoCard;
